import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { CheckCircle, MessageCircle, MapPin, Calendar, FileText } from 'lucide-react';
import AddToCalendar from '../components/AddToCalendar';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function BookingConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [booking, setBooking] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      checkPaymentAndFetchBooking();
    }
  }, [sessionId]);

  const checkPaymentAndFetchBooking = async () => {
    try {
      // Check payment status
      const paymentRes = await apiClient.get(`/payments/status/${sessionId}`);
      
      if (paymentRes.data.payment_status === 'paid') {
        // Get booking from transaction
        const bookingsRes = await apiClient.get('/bookings');
        const userBooking = bookingsRes.data[bookingsRes.data.length - 1]; // Get latest
        setBooking(userBooking);

        // Get artist details
        if (userBooking) {
          const artistRes = await apiClient.get(`/artists/${userBooking.artist_id}`);
          setArtist(artistRes.data);
        }
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Card className="glass-default border-white/10 max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-xl text-white/60">Booking not found</p>
            <Button onClick={() => navigate('/browse')} className="mt-4">
              Back to Browse
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const eventLocation = booking.event_location || { lat: 40.7128, lng: -74.0060 }; // Default to NYC

  return (
    <div className="min-h-screen pt-20 relative">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-5xl">
        {/* Success Header */}
        <div className="glass-default rounded-2xl p-8 mb-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-400" size={48} />
          </div>
          <h1 className="text-4xl font-bold mb-2">Booking Confirmed!</h1>
          <p className="text-white/60 text-lg">Your payment was successful. The artist will be notified.</p>
          <p className="text-violet-400 text-xl font-semibold mt-4">Thank you for working with RazientSounds!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Booking Details */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-white/60 mb-1">Booking ID</p>
                <p className="font-mono text-sm">{booking.booking_id}</p>
              </div>
              
              <div className="flex items-center gap-3 p-3 glass-light rounded-lg">
                <Calendar className="text-violet-400" size={20} />
                <div>
                  <p className="text-sm text-white/60">Event Date</p>
                  <p className="font-medium">{new Date(booking.event_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-white/60 mb-2">Duration</p>
                <p className="font-medium">{booking.event_duration} hours</p>
              </div>

              <div>
                <p className="text-sm text-white/60 mb-2">Type</p>
                <p className="font-medium">{booking.is_local ? 'Local Event' : 'Travel Required'}</p>
              </div>

              {artist && (
                <Button
                  data-testid="message-artist-button"
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-violet-600 hover:bg-violet-500 mt-4"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Message Artist via Dashboard
                </Button>
              )}

              {/* Add to Calendar */}
              {booking && (
                <div className="mt-4">
                  <AddToCalendar
                    event={{
                      title: `${artist?.bio?.slice(0, 30) || 'Artist'} Performance`,
                      description: `Booking ID: ${booking.booking_id}\nEvent Type: ${booking.event_type || 'Performance'}\nDuration: ${booking.event_duration} hours`,
                      location: booking.venue || booking.event_location || '',
                      startDate: new Date(booking.event_date),
                      endDate: new Date(new Date(booking.event_date).getTime() + (booking.event_duration || 2) * 60 * 60 * 1000),
                      uid: `booking-${booking.booking_id}@razientsounds.com`
                    }}
                    className="w-full"
                  />
                </div>
              )}

              {/* Contract Management */}
              {booking && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/booking/${booking.booking_id}/contract`)}
                  className="w-full mt-2"
                  data-testid="view-contract-btn"
                >
                  <FileText size={18} className="mr-2" />
                  View / Sign Contract
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Map */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="text-violet-400" />
                Event Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.event_location ? (
                <div className="space-y-4">
                  <div className="p-3 glass-light rounded-lg">
                    <p className="text-sm text-white/60 mb-1">Address</p>
                    <p className="font-medium">{booking.event_location.address || 'Address not provided'}</p>
                  </div>
                  <div className="h-64 rounded-lg overflow-hidden">
                    <MapContainer
                      center={[eventLocation.lat, eventLocation.lng]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <Marker position={[eventLocation.lat, eventLocation.lng]}>
                        <Popup>Event Location</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-white/40">
                  <MapPin size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No location specified for this booking</p>
                  <p className="text-sm mt-2">Coordinate with the artist via messages</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="glass-default border-white/10 mt-6">
          <CardHeader>
            <CardTitle>What&apos;s Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-violet-400">1</span>
                </div>
                <h3 className="font-semibold mb-2">Artist Confirmation</h3>
                <p className="text-sm text-white/60">The artist will review and confirm your booking request</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-violet-400">2</span>
                </div>
                <h3 className="font-semibold mb-2">Coordinate Details</h3>
                <p className="text-sm text-white/60">Use the message system to discuss event specifics and timing</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-violet-400">3</span>
                </div>
                <h3 className="font-semibold mb-2">Enjoy Your Event</h3>
                <p className="text-sm text-white/60">Leave a review after the performance to help others</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center gap-4 mt-8">
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
          >
            View My Bookings
          </Button>
          <Button
            onClick={() => navigate('/browse')}
            variant="outline"
            className="border-white/20 hover:bg-white/10"
          >
            Browse More Artists
          </Button>
        </div>
      </div>
    </div>
  );
}
